import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  CaretLeft,
  FloppyDisk,
  Trash,
  Check,
  X,
  Plus,
  Image,
  ClipboardText,
  UploadSimple,
  GoogleDriveLogo,
  FileText as FileTextIcon,
  Eye,
  Spinner,
  FolderOpen,
  MapPin,
  Wrench,
  PencilSimple,
  Megaphone,
  ShareNetwork,
  Copy,
  QrCode,
  DownloadSimple
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS_LABELS = {
  en_stock: "En stock", en_preparation: "En préparation", pret_a_vendre: "Prêt à vendre",
  reserve: "Réservé", vendu: "Vendu", archive: "Archivé"
};
const PREP_STEPS = ["mecanique", "carrosserie", "nettoyage", "photos", "annonce"];
const PREP_STEP_LABELS = { mecanique: "Mécanique", carrosserie: "Carrosserie", nettoyage: "Nettoyage", photos: "Photos", annonce: "Annonce" };
const PREP_STATUS_LABELS = { a_faire: "À faire", en_cours: "En cours", termine: "Terminé", bloque: "Bloqué" };
const TASK_STATUS_COLORS = {
  a_faire: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  en_cours: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  termine: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  bloque: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};
const FUEL_OPTIONS = ["Essence", "Diesel", "Hybride", "Électrique", "GPL", "CNG"];
const TRANSMISSION_OPTIONS = ["Manuelle", "Automatique", "Semi-automatique"];
const AUDIT_STATUS_COLORS = {
  ok: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  a_prevoir: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  probleme: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  non_verifie: "bg-slate-100 text-slate-500 dark:bg-slate-700/40 dark:text-slate-400",
};
const AUDIT_STATUS_LABELS = { ok: "OK", a_prevoir: "A prevoir", probleme: "Probleme", non_verifie: "Non verifie" };
const DECISION_COLORS = {
  acheter: "bg-emerald-600 hover:bg-emerald-700 text-white",
  negocier: "bg-amber-500 hover:bg-amber-600 text-white",
  refuser: "bg-red-600 hover:bg-red-700 text-white",
  en_attente: "bg-slate-200 hover:bg-slate-300 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
};
const DECISION_LABELS = { acheter: "Acheter", negocier: "Negocier", refuser: "Refuser", en_attente: "En attente" };
const CAT_ICONS = { documents: ClipboardText, mecanique: Wrench, electronique: Eye, essai_routier: MapPin, carrosserie: Image, interieur: Image, informations: Eye };
const DOC_FIELDS = [
  { key: "certificat_immat_1", label: "Certificat d'immatriculation 1" },
  { key: "certificat_immat_2", label: "Certificat d'immatriculation 2" },
  { key: "certificat_conformite", label: "Certificat de conformité" },
  { key: "controle_technique", label: "Contrôle technique" },
  { key: "carpass", label: "Car-Pass" },
  { key: "papier_vente_achat", label: "Papier de vente / achat" }
];

/* ─── Sub-components ─── */

const VEHICLE_OPTIONS = [
  {cat:"Aides a la conduite",color:"bg-blue-50 dark:bg-blue-900/20",badge:"bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200",dot:"bg-blue-500",items:["Regulateur de vitesse","Regulateur adaptatif","Limiteur de vitesse","Aide au maintien de voie","Detection des panneaux routiers","Alerte angle mort","Freinage d'urgence automatique","Aide au demarrage en cote","Capteurs de stationnement avant","Capteurs de stationnement arriere","Camera de recul","Camera 360","Park Assist","Direction assistee","ESP","ABS"]},
  {cat:"Confort",color:"bg-emerald-50 dark:bg-emerald-900/20",badge:"bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200",dot:"bg-emerald-500",items:["Climatisation","Climatisation automatique","Climatisation bi-zone","Sieges chauffants","Sieges ventiles","Sieges electriques","Memoire de siege","Volant chauffant","Volant multifonction","Accoudoir","Vitres electriques","Retroviseurs electriques","Retroviseurs rabattables","Ouverture sans cle","Demarrage sans cle","Fermeture centralisee","Hayon electrique","Chauffage auxiliaire"]},
  {cat:"Multimedia",color:"bg-violet-50 dark:bg-violet-900/20",badge:"bg-violet-100 text-violet-700 dark:bg-violet-800 dark:text-violet-200",dot:"bg-violet-500",items:["GPS","Ecran tactile","Ecran digital","Bluetooth","Apple CarPlay","Android Auto","USB","Radio DAB","Systeme audio premium","Commande vocale","Chargeur smartphone a induction","CD","Fonction TV"]},
  {cat:"Exterieur",color:"bg-amber-50 dark:bg-amber-900/20",badge:"bg-amber-100 text-amber-700 dark:bg-amber-800 dark:text-amber-200",dot:"bg-amber-500",items:["Jantes alliage","Jantes sport","Toit panoramique","Toit ouvrant","Vitres teintees","Attelage","Barres de toit","Pack sport","Pack AMG","Pack S Line","Pack GT Line","Retroviseurs chauffants"]},
  {cat:"Eclairage",color:"bg-yellow-50 dark:bg-yellow-900/20",badge:"bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200",dot:"bg-yellow-500",items:["Feux LED","Feux xenon","Feux anti-brouillard","Feux de jour LED","Feux automatiques","Feux de route automatiques","Eclairage d'ambiance"]},
  {cat:"Interieur",color:"bg-rose-50 dark:bg-rose-900/20",badge:"bg-rose-100 text-rose-700 dark:bg-rose-800 dark:text-rose-200",dot:"bg-rose-500",items:["Sellerie cuir","Sellerie alcantara","Sellerie tissu","Volant sport","Pedalier sport","Inserts aluminium","Inserts bois","Sieges sport"]},
  {cat:"Securite",color:"bg-red-50 dark:bg-red-900/20",badge:"bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-200",dot:"bg-red-500",items:["Airbags","Isofix","Alarme","Antidemarrage","Controle de traction","Surveillance pression pneus"]},
  {cat:"Energie",color:"bg-teal-50 dark:bg-teal-900/20",badge:"bg-teal-100 text-teal-700 dark:bg-teal-800 dark:text-teal-200",dot:"bg-teal-500",items:["Start & Stop","Mode eco","Charge bidirectionnelle","Certificat batterie","Fonctionne au biodiesel"]},
  {cat:"Divers",color:"bg-slate-50 dark:bg-slate-800/40",badge:"bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",dot:"bg-slate-500",items:["Equipement handicape","1er proprietaire","Non-fumeur","Carnet d'entretien","Entretien complet","Pneus neufs","Freins neufs","Distribution faite","Garantie","Vehicule accidente","Vehicule importe"]}
];

const EquipementsSection = ({ vehicleForm, setVehicleForm, canEdit }) => {
  const [collapsed, setCollapsed] = useState({});
  const selected = vehicleForm.equipements || [];
  const totalCount = selected.length;

  const toggle = (item) => {
    if (!canEdit) return;
    const updated = selected.includes(item) ? selected.filter(i => i !== item) : [...selected, item];
    setVehicleForm({ ...vehicleForm, equipements: updated });
  };

  const toggleCat = (cat) => setCollapsed(p => ({ ...p, [cat]: !p[cat] }));
  const catCount = (items) => items.filter(i => selected.includes(i)).length;

  return (
    <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700" data-testid="equipements-section">
      <div className="flex items-center justify-between mb-4">
        <Label className="form-label text-base">Equipements & Options</Label>
        {totalCount > 0 && <span className="text-xs font-semibold bg-blue-600 text-white px-2.5 py-1 rounded-full">{totalCount} selectionne(s)</span>}
      </div>
      <div className="space-y-2">
        {VEHICLE_OPTIONS.map(({ cat, color, badge, dot, items }) => {
          const count = catCount(items);
          const isOpen = !collapsed[cat];
          return (
            <div key={cat} className={`rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 ${color}`}>
              <button type="button" onClick={() => toggleCat(cat)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left" data-testid={`cat-${cat}`}>
                <div className={`w-2.5 h-2.5 rounded-full ${dot}`} />
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex-1">{cat}</span>
                {count > 0 && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge}`}>{count}/{items.length}</span>}
                <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {isOpen && (
                <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                  {items.map(item => {
                    const checked = selected.includes(item);
                    return (
                      <label key={item} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer text-xs transition-all select-none ${checked ? `${badge} font-medium` : 'hover:bg-white/60 dark:hover:bg-slate-700/40 text-slate-600 dark:text-slate-400'} ${!canEdit ? 'opacity-60 cursor-default' : ''}`} data-testid={`opt-${item.replace(/\s/g,'-')}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggle(item)} disabled={!canEdit} className="sr-only" />
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${checked ? 'bg-current border-transparent' : 'border-slate-300 dark:border-slate-600'}`}>
                          {checked && <Check size={10} className="text-white" />}
                        </div>
                        {item}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


const MarginBlock = ({ vehicleId }) => {
  const [margin, setMargin] = useState(null);
  const fmt = (v) => new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(v || 0);

  useEffect(() => {
    if (!vehicleId) return;
    axios.get(`${API}/vehicles/${vehicleId}/margin`)
      .then(res => setMargin(res.data))
      .catch(() => {});
  }, [vehicleId]);

  if (!margin || !margin.prix_vente) return null;

  const isProfit = margin.marge > 0;
  const isLoss = margin.marge < 0;

  return (
    <div className={`mt-4 rounded-xl p-4 border-2 ${isProfit ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/10 dark:border-emerald-700" : isLoss ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-700" : "border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700"}`} data-testid="margin-block">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Calcul de marge</span>
        <span className={`text-lg font-bold ${isProfit ? "text-emerald-700 dark:text-emerald-400" : isLoss ? "text-red-700 dark:text-red-400" : "text-slate-600"}`}>
          {isProfit ? "+" : ""}{fmt(margin.marge)}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-2.5">
          <p className="text-slate-400 mb-0.5">Prix achat</p>
          <p className="font-semibold text-slate-700 dark:text-slate-200">{fmt(margin.prix_achat)}</p>
        </div>
        <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-2.5">
          <p className="text-slate-400 mb-0.5">Frais prep.</p>
          <p className="font-semibold text-slate-700 dark:text-slate-200">{fmt(margin.frais_vehicule + margin.frais_preparation)}</p>
        </div>
        <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-2.5">
          <p className="text-slate-400 mb-0.5">Cout total</p>
          <p className="font-bold text-slate-900 dark:text-slate-100">{fmt(margin.cout_total)}</p>
        </div>
        <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-2.5">
          <p className="text-slate-400 mb-0.5">Prix vente</p>
          <p className="font-bold text-slate-900 dark:text-slate-100">{fmt(margin.prix_vente)}</p>
        </div>
      </div>
    </div>
  );
};

const VehicleInfoTab = ({ vehicleForm, setVehicleForm, canEdit, canSeePrices, saving, onSave, prepProgression, vehicleId }) => {
  const prepReady = prepProgression >= 100;
  const handleStatusChange = (val) => {
    if (val === "pret_a_vendre" && !prepReady) {
      return; // blocked
    }
    setVehicleForm({ ...vehicleForm, statut: val });
  };
  return (
  <div className="card p-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <div><Label className="form-label">Marque</Label><Input value={vehicleForm.marque || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, marque: e.target.value })} disabled={!canEdit} data-testid="edit-marque" /></div>
      <div><Label className="form-label">Modèle</Label><Input value={vehicleForm.modele || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, modele: e.target.value })} disabled={!canEdit} data-testid="edit-modele" /></div>
      <div><Label className="form-label">Version</Label><Input value={vehicleForm.version || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, version: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">VIN</Label><Input value={vehicleForm.vin || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, vin: e.target.value })} disabled={!canEdit} className="font-mono text-xs" /></div>
      <div><Label className="form-label">Immatriculation</Label><Input value={vehicleForm.immatriculation || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, immatriculation: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Kilométrage</Label><Input type="number" value={vehicleForm.kilometrage || 0} onChange={(e) => setVehicleForm({ ...vehicleForm, kilometrage: parseInt(e.target.value) || 0 })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Carburant</Label>
        <Select value={vehicleForm.carburant} onValueChange={(val) => setVehicleForm({ ...vehicleForm, carburant: val })} disabled={!canEdit}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{FUEL_OPTIONS.map(o => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div><Label className="form-label">Boîte</Label>
        <Select value={vehicleForm.boite} onValueChange={(val) => setVehicleForm({ ...vehicleForm, boite: val })} disabled={!canEdit}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{TRANSMISSION_OPTIONS.map(o => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div><Label className="form-label">Année</Label><Input type="number" value={vehicleForm.annee || 2024} onChange={(e) => setVehicleForm({ ...vehicleForm, annee: parseInt(e.target.value) || 2024 })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Couleur</Label><Input value={vehicleForm.couleur || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, couleur: e.target.value })} disabled={!canEdit} /></div>
      <div><Label className="form-label">Statut</Label>
        <Select value={vehicleForm.statut} onValueChange={handleStatusChange} disabled={!canEdit}>
          <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k} disabled={k === "pret_a_vendre" && !prepReady}>
                {v}{k === "pret_a_vendre" && !prepReady ? " (prep. requise)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!prepReady && vehicleForm.statut !== "pret_a_vendre" && (
          <p className="text-xs text-amber-600 mt-1">Préparation à {prepProgression}% — terminez toutes les étapes pour activer "Prêt à vendre"</p>
        )}
      </div>
    </div>
    {canSeePrices && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6 pt-6 border-t border-slate-200">
        <div><Label className="form-label">Prix d'achat (EUR)</Label><Input type="number" step="0.01" value={vehicleForm.prix_achat || 0} onChange={(e) => setVehicleForm({ ...vehicleForm, prix_achat: parseFloat(e.target.value) || 0 })} disabled={!canEdit} /></div>
        <div><Label className="form-label">Frais (EUR)</Label><Input type="number" step="0.01" value={vehicleForm.frais || 0} onChange={(e) => setVehicleForm({ ...vehicleForm, frais: parseFloat(e.target.value) || 0 })} disabled={!canEdit} /></div>
        <div><Label className="form-label">Prix de vente (EUR)</Label><Input type="number" step="0.01" value={vehicleForm.prix_vente || 0} onChange={(e) => setVehicleForm({ ...vehicleForm, prix_vente: parseFloat(e.target.value) || 0 })} disabled={!canEdit} /></div>
      </div>
    )}
    {/* Margin Block */}
    {canSeePrices && <MarginBlock vehicleId={vehicleId} />}
    {/* Equipements / Options */}
    <EquipementsSection vehicleForm={vehicleForm} setVehicleForm={setVehicleForm} canEdit={canEdit} />
    <div className="mt-6 pt-6 border-t border-slate-200">
      <Label className="form-label">Observations internes</Label>
      <Textarea value={vehicleForm.observations_internes || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, observations_internes: e.target.value })} disabled={!canEdit} rows={3} />
    </div>
    {canEdit && (
      <div className="flex justify-end mt-6">
        <Button onClick={onSave} disabled={saving} className="btn-primary" data-testid="save-vehicle-btn">
          <FloppyDisk size={18} className="mr-2" />{saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    )}
  </div>
  );
};

const PreparationTab = ({ vehicleId, canEditPrep, prepTasks, setPrepTasks, prestataires, prepTypes, localisation, onRefreshData }) => {
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ type_prestation: "carrosserie", prestataire_id: "", statut: "a_faire", date_debut: "", date_fin_prevue: "", prix: "", notes: "" });

  const typeLabels = {};
  prepTypes.forEach(t => { typeLabels[t.type_id] = t.label; });

  const openAddTask = () => {
    setTaskForm({ type_prestation: prepTypes[0]?.type_id || "carrosserie", prestataire_id: "", statut: "a_faire", date_debut: "", date_fin_prevue: "", prix: "", notes: "" });
    setEditTask(null);
    setTaskDialogOpen(true);
  };

  const openEditTask = (task) => {
    setTaskForm({
      type_prestation: task.type_prestation || "carrosserie",
      prestataire_id: task.prestataire_id || "",
      statut: task.statut || "a_faire",
      date_debut: task.date_debut ? task.date_debut.slice(0, 10) : "",
      date_fin_prevue: task.date_fin_prevue ? task.date_fin_prevue.slice(0, 10) : "",
      prix: task.prix || "",
      notes: task.notes || ""
    });
    setEditTask(task);
    setTaskDialogOpen(true);
  };

  const handleSaveTask = async (e) => {
    e.preventDefault();
    const payload = { ...taskForm, prix: taskForm.prix ? parseFloat(taskForm.prix) : null, date_debut: taskForm.date_debut || null, date_fin_prevue: taskForm.date_fin_prevue || null };
    if (!payload.prestataire_id) delete payload.prestataire_id;
    try {
      if (editTask) {
        await axios.put(`${API}/vehicles/${vehicleId}/preparation-tasks/${editTask.task_id}`, payload);
        toast.success("Tâche mise à jour");
      } else {
        await axios.post(`${API}/vehicles/${vehicleId}/preparation-tasks`, payload);
        toast.success("Tâche ajoutée");
      }
      setTaskDialogOpen(false);
      onRefreshData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleQuickStatus = async (task, newStatus) => {
    try {
      await axios.put(`${API}/vehicles/${vehicleId}/preparation-tasks/${task.task_id}`, { statut: newStatus });
      toast.success(newStatus === "termine" ? "Terminé !" : "Statut mis à jour");
      onRefreshData();
    } catch { toast.error("Erreur"); }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}/preparation-tasks/${taskId}`);
      toast.success("Tâche supprimée");
      onRefreshData();
    } catch { toast.error("Erreur"); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' }) : null;

  const byStatus = { a_faire: [], en_cours: [], termine: [], bloque: [] };
  prepTasks.forEach(t => { (byStatus[t.statut] || byStatus.a_faire).push(t); });
  const totalCost = prepTasks.reduce((s, t) => s + (t.prix || 0), 0);

  return (
    <div className="space-y-6">
      {/* Localisation */}
      <div className="card p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${localisation?.type_loc === "chez_prestataire" ? "bg-amber-100 dark:bg-amber-900/40" : "bg-emerald-100 dark:bg-emerald-900/40"}`}>
            <MapPin size={20} className={localisation?.type_loc === "chez_prestataire" ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300"} />
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Localisation actuelle</p>
            <p className="font-semibold text-sm text-slate-900 dark:text-slate-100" data-testid="vehicle-location">
              {localisation?.type_loc === "chez_prestataire"
                ? `Chez ${localisation.prestataire_nom || "prestataire"}`
                : "Au garage"}
            </p>
          </div>
        </div>
        {totalCost > 0 && (
          <div className="text-right">
            <p className="text-xs text-slate-500 dark:text-slate-400">Coût total prépa</p>
            <p className="font-bold text-sm text-slate-900 dark:text-slate-100">{totalCost.toLocaleString('fr-BE')} EUR</p>
          </div>
        )}
      </div>

      {/* Header + Add */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {Object.entries(byStatus).map(([status, tasks]) => tasks.length > 0 && (
            <span key={status} className={`px-2.5 py-1 text-xs font-semibold rounded-full ${TASK_STATUS_COLORS[status]}`}>
              {tasks.length} {PREP_STATUS_LABELS[status]}
            </span>
          ))}
        </div>
        {canEditPrep && (
          <Button onClick={openAddTask} className="btn-primary" data-testid="add-prep-task-btn">
            <Plus size={16} className="mr-1.5" /> Ajouter une tâche
          </Button>
        )}
      </div>

      {/* Task Cards */}
      {prepTasks.length === 0 ? (
        <div className="card p-12 text-center">
          <Wrench size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Aucune tâche de préparation</p>
          {canEditPrep && <p className="text-xs text-slate-400 mt-1">Cliquez sur "Ajouter une tâche" pour commencer</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {prepTasks.map(task => (
            <div key={task.task_id} data-testid={`prep-task-card-${task.task_id}`}
              className={`card p-5 transition-all duration-200 hover:shadow-md border-l-4 ${
                task.statut === "termine" ? "border-l-emerald-500" :
                task.statut === "en_cours" ? "border-l-blue-500" :
                task.statut === "bloque" ? "border-l-red-500" :
                "border-l-amber-400"
              }`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                    {typeLabels[task.type_prestation] || task.type_prestation}
                  </h4>
                  {task.prestataire_nom && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{task.prestataire_nom}</p>
                  )}
                </div>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${TASK_STATUS_COLORS[task.statut]}`}>
                  {PREP_STATUS_LABELS[task.statut]}
                </span>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mb-3">
                {(task.date_debut || task.date_fin_prevue) && (
                  <span>{fmtDate(task.date_debut)}{task.date_fin_prevue ? ` → ${fmtDate(task.date_fin_prevue)}` : ""}</span>
                )}
                {task.prix != null && task.prix > 0 && (
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{task.prix.toLocaleString('fr-BE')} EUR</span>
                )}
              </div>

              {task.notes && (
                <p className="text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded p-2 mb-3">{task.notes}</p>
              )}

              {canEditPrep && (
                <div className="flex gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => openEditTask(task)}>
                    <PencilSimple size={13} className="mr-1" /> Modifier
                  </Button>
                  {task.statut !== "termine" && (
                    <Button size="sm" variant="ghost" className="text-xs h-7 text-emerald-600 hover:text-emerald-700"
                      onClick={() => handleQuickStatus(task, "termine")}>
                      <Check size={13} className="mr-1" /> Terminé
                    </Button>
                  )}
                  {task.statut === "a_faire" && (
                    <Button size="sm" variant="ghost" className="text-xs h-7 text-blue-600 hover:text-blue-700"
                      onClick={() => handleQuickStatus(task, "en_cours")}>
                      En cours
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-xs h-7 text-red-500 hover:text-red-600 ml-auto">
                        <Trash size={13} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Supprimer cette tâche ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteTask(task.task_id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editTask ? "Modifier la tâche" : "Nouvelle tâche de préparation"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveTask} className="space-y-4">
            <div>
              <Label className="form-label">Type de prestation</Label>
              <Select value={taskForm.type_prestation} onValueChange={v => setTaskForm({ ...taskForm, type_prestation: v })}>
                <SelectTrigger data-testid="task-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {prepTypes.map(t => <SelectItem key={t.type_id} value={t.type_id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Prestataire</Label>
              <Select value={taskForm.prestataire_id || "none"} onValueChange={v => setTaskForm({ ...taskForm, prestataire_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="task-prestataire"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun (interne)</SelectItem>
                  {prestataires.map(p => <SelectItem key={p.prestataire_id} value={p.prestataire_id}>{p.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Statut</Label>
              <Select value={taskForm.statut} onValueChange={v => setTaskForm({ ...taskForm, statut: v })}>
                <SelectTrigger data-testid="task-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_faire">À faire</SelectItem>
                  <SelectItem value="en_cours">En cours</SelectItem>
                  <SelectItem value="termine">Terminé</SelectItem>
                  <SelectItem value="bloque">Bloqué</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Date début</Label>
                <Input type="date" data-testid="task-date-debut" value={taskForm.date_debut} onChange={e => setTaskForm({ ...taskForm, date_debut: e.target.value })} />
              </div>
              <div>
                <Label className="form-label">Date fin prévue</Label>
                <Input type="date" data-testid="task-date-fin" value={taskForm.date_fin_prevue} onChange={e => setTaskForm({ ...taskForm, date_fin_prevue: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="form-label">Prix (EUR)</Label>
              <Input type="number" step="0.01" data-testid="task-prix" value={taskForm.prix} onChange={e => setTaskForm({ ...taskForm, prix: e.target.value })} placeholder="0.00" />
            </div>
            <div>
              <Label className="form-label">Notes</Label>
              <Textarea data-testid="task-notes" value={taskForm.notes} onChange={e => setTaskForm({ ...taskForm, notes: e.target.value })} placeholder="Détails de la prestation..." rows={3} />
            </div>
            <Button type="submit" className="btn-primary w-full" data-testid="save-task-btn">
              {editTask ? "Mettre à jour" : "Ajouter la tâche"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DocumentsTab = ({ docsForm, setDocsForm, canEdit, saving, onSave, driveConnected, onDriveUpload, driveDocuments, onDeleteDriveFile }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = { current: null };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      await onDriveUpload(file, "documents");
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
  <div className="card p-6">
    <div className="space-y-3">
      {DOC_FIELDS.map((doc) => (
        <div key={doc.key} className="flex items-center gap-4 p-3 rounded-lg border border-slate-200">
          <Checkbox checked={!!docsForm[doc.key]} onCheckedChange={(checked) => setDocsForm({ ...docsForm, [doc.key]: checked })} disabled={!canEdit} data-testid={`doc-${doc.key}`} />
          <span className="flex-1 text-sm text-slate-900">{doc.label}</span>
          {docsForm[doc.key] ? <Check size={18} className="text-emerald-600" /> : <X size={18} className="text-slate-300" />}
        </div>
      ))}
      <div className="flex items-center gap-4 p-3 rounded-lg border border-slate-200">
        <Label className="w-32 text-sm">Nombre de clés</Label>
        <Input type="number" min="0" className="w-20" value={docsForm.nb_cles ?? 1} onChange={(e) => setDocsForm({ ...docsForm, nb_cles: parseInt(e.target.value) || 0 })} disabled={!canEdit} />
      </div>
      <div className="flex items-center gap-4 p-3 rounded-lg border border-slate-200">
        <Label className="w-32 text-sm">Lien Car-Pass</Label>
        <Input className="flex-1" placeholder="https://..." value={docsForm.lien_carpass || ""} onChange={(e) => setDocsForm({ ...docsForm, lien_carpass: e.target.value })} disabled={!canEdit} />
      </div>
      <div className="flex items-center gap-4 p-4 rounded-lg border-2 border-slate-300 bg-slate-50">
        <Checkbox checked={!!docsForm.papiers_complets} onCheckedChange={(checked) => setDocsForm({ ...docsForm, papiers_complets: checked })} disabled={!canEdit} data-testid="doc-papiers-complets" />
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Tous les papiers sont là</h3>
          <p className="text-caption text-slate-500">Cochez quand tous les documents sont complets</p>
        </div>
      </div>

      {/* Drive Documents Section */}
      {driveConnected && (
        <div className="pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
              <GoogleDriveLogo size={18} className="text-blue-600" /> Documents Google Drive
            </h3>
            {canEdit && (
              <>
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" multiple onChange={handleFileUpload} className="hidden" ref={(el) => fileInputRef.current = el} data-testid="drive-doc-file-input" />
                <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="upload-drive-doc-btn">
                  {uploading ? <Spinner size={14} className="mr-1 animate-spin" /> : <UploadSimple size={14} className="mr-1" />}
                  {uploading ? "Upload..." : "Upload document"}
                </Button>
              </>
            )}
          </div>
          {driveDocuments && driveDocuments.length > 0 ? (
            <div className="space-y-2">
              {driveDocuments.map((doc) => (
                <div key={doc.doc_id || doc.drive_file_id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50">
                  <FileTextIcon size={20} className="text-blue-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{doc.name}</p>
                    <p className="text-xs text-slate-500">{doc.size ? `${(doc.size / 1024).toFixed(0)} Ko` : ""}</p>
                  </div>
                  {doc.web_view_link && (
                    <a href={doc.web_view_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 p-1" title="Voir sur Drive">
                      <Eye size={16} />
                    </a>
                  )}
                  {canEdit && (
                    <button onClick={() => onDeleteDriveFile(doc.drive_file_id)} className="text-red-500 hover:text-red-600 p-1" data-testid={`delete-doc-${doc.doc_id}`}>
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-3">Aucun document uploadé sur Drive</p>
          )}
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end pt-2">
          <Button onClick={onSave} disabled={saving} className="btn-primary" data-testid="save-documents-btn">
            <FloppyDisk size={18} className="mr-2" />{saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      )}
    </div>
  </div>
  );
};

const AI_STATUS_CONFIG = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300" },
  analyzing: { label: "Analyse...", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  done: { label: "Terminé", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  error: { label: "Erreur", color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
};
const QUALITY_LABELS = { bonne: "Bonne", moyenne: "Moyenne", faible: "Faible", sombre: "Sombre", surexposee: "Surexposée", bon: "Bon", decentre: "Décentré", trop_loin: "Trop loin", trop_proche: "Trop proche" };
const CATEGORY_LABELS = { exterieur_avant: "Ext. Avant", exterieur_arriere: "Ext. Arrière", exterieur_lateral: "Ext. Latéral", interieur_avant: "Int. Avant", interieur_arriere: "Int. Arrière", tableau_de_bord: "Tableau de bord", moteur: "Moteur", roue_pneu: "Roue/Pneu", coffre: "Coffre", detail_dommage: "Dommage", document: "Document", autre: "Autre" };
const STATUT_LABELS_AI = { validee: "Validée", corrigee: "Corrigée", a_refaire: "À refaire", a_corriger: "À corriger", en_attente: "En attente" };
const STATUT_COLORS_AI = { validee: "bg-emerald-500", corrigee: "bg-blue-500", a_refaire: "bg-red-500", a_corriger: "bg-amber-500" };
const CATEGORY_FILTERS = [
  { value: "all", label: "Toutes" },
  { value: "exterieur_avant", label: "Ext. Avant" },
  { value: "exterieur_arriere", label: "Ext. Arrière" },
  { value: "exterieur_lateral", label: "Ext. Latéral" },
  { value: "interieur_avant", label: "Int. Avant" },
  { value: "interieur_arriere", label: "Int. Arrière" },
  { value: "tableau_de_bord", label: "Tableau de bord" },
  { value: "moteur", label: "Moteur" },
  { value: "roue_pneu", label: "Roue/Pneu" },
  { value: "coffre", label: "Coffre" },
  { value: "detail_dommage", label: "Dommage" },
  { value: "autre", label: "Autre" },
];

const PhotoAIBadge = ({ status }) => {
  const cfg = AI_STATUS_CONFIG[status] || AI_STATUS_CONFIG.pending;
  const isActive = status === "pending" || status === "analyzing";
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color} ${isActive ? "animate-pulse" : ""}`} data-testid={`ai-status-badge-${status}`}>
      {status === "done" ? <Check size={10} weight="bold" /> : status === "error" ? <X size={10} weight="bold" /> : <Spinner size={10} className="animate-spin" />}
      {cfg.label}
    </span>
  );
};

const AIProgressSteps = ({ status }) => {
  const steps = [
    { key: "upload", label: "Upload" },
    { key: "analyze", label: "Analyse IA" },
    { key: "correct", label: "Corrections" },
    { key: "done", label: "Terminé" },
  ];
  const activeIdx = status === "pending" ? 0 : status === "analyzing" ? 1 : status === "done" || status === "error" ? 3 : 0;
  return (
    <div className="flex items-center gap-1 w-full mt-2" data-testid="ai-progress-steps">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center flex-1">
          <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${i <= activeIdx ? (status === "error" && i === 3 ? "bg-red-400" : "bg-blue-500") : "bg-slate-200 dark:bg-slate-700"}`} />
        </div>
      ))}
    </div>
  );
};

const PhotoDetailModal = ({ photo, onClose, onReAnalyze }) => {
  const [showCorrected, setShowCorrected] = useState(true);
  if (!photo) return null;
  const analysis = photo.ai_analysis || {};
  const qualite = analysis.qualite || {};
  const elements = analysis.elements_sensibles || {};
  const corrections = analysis.corrections_appliquees || photo.corrections_appliquees || [];
  const statut = analysis.statut || "en_attente";
  const correctedUrl = analysis.photo_corrigee_url || photo.photo_corrigee_url;

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="photo-detail-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3 flex-wrap">
          <span>{CATEGORY_LABELS[analysis.categorie] || analysis.categorie || "Non classée"}</span>
          <span className={`text-xs px-2 py-1 rounded-full text-white ${STATUT_COLORS_AI[statut] || "bg-gray-500"}`} data-testid="ai-photo-statut">
            {STATUT_LABELS_AI[statut] || statut}
          </span>
          {photo.is_principale && <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">Principale</span>}
          {photo.nom_fichier_ia && <span className="text-xs text-slate-400 font-normal" data-testid="ai-filename">{photo.nom_fichier_ia}</span>}
        </DialogTitle>
        <DialogDescription className="sr-only">Détails et analyse IA de la photo véhicule</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="space-y-2">
          {correctedUrl ? (
            <div>
              <div className="flex gap-2 mb-2">
                <button onClick={() => setShowCorrected(false)} className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${!showCorrected ? "bg-slate-800 text-white dark:bg-white dark:text-slate-900" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`} data-testid="btn-show-original">Originale</button>
                <button onClick={() => setShowCorrected(true)} className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${showCorrected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`} data-testid="btn-show-corrected">Corrigée IA</button>
              </div>
              <img src={showCorrected ? correctedUrl : photo.url} alt="Photo véhicule" className="w-full rounded-lg border border-slate-200 dark:border-slate-700 max-h-[400px] object-contain bg-slate-50 dark:bg-slate-800" data-testid="photo-preview-img" />
              <p className="text-xs text-center mt-1 text-slate-500">{showCorrected ? "Version corrigée par l'IA" : "Photo originale"}</p>
            </div>
          ) : (
            <img src={photo.url} alt="Photo véhicule" className="w-full rounded-lg border max-h-[400px] object-contain bg-slate-50 dark:bg-slate-800" />
          )}
        </div>
        <div className="space-y-4">
          {analysis.description && (
            <div><p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</p><p className="text-sm text-slate-600 dark:text-slate-400" data-testid="ai-description">{analysis.description}</p></div>
          )}
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Qualité</p>
            <div className="grid grid-cols-2 gap-2">
              {[["Netteté", qualite.nettete], ["Luminosité", qualite.luminosite], ["Cadrage", qualite.cadrage], ["Exploitable", qualite.exploitable === true ? "Oui" : qualite.exploitable === false ? "Non" : "-"]].map(([label, val]) => (
                <div key={label} className="flex justify-between items-center bg-slate-50 dark:bg-slate-800 px-3 py-2 rounded-md" data-testid={`quality-${label.toLowerCase()}`}>
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className={`text-xs font-medium ${val === "bonne" || val === "bon" || val === "Oui" ? "text-emerald-600" : val === "moyenne" || val === "decentre" ? "text-amber-600" : val === "faible" || val === "sombre" || val === "surexposee" || val === "Non" ? "text-red-600" : "text-slate-700 dark:text-slate-300"}`}>
                    {QUALITY_LABELS[val] || val || "-"}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Éléments sensibles</p>
            <div className="flex gap-2 flex-wrap">
              {[["Plaque", elements.plaque], ["Visage", elements.visage], ["Document", elements.document]].map(([label, detected]) => (
                <span key={label} className={`text-xs px-2.5 py-1 rounded-full font-medium ${detected ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"}`} data-testid={`sensitive-${label.toLowerCase()}`}>
                  {detected ? "! " : ""}{label}: {detected ? "Détecté" : "OK"}
                </span>
              ))}
            </div>
          </div>
          {corrections.length > 0 && (
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Corrections appliquées</p>
              <div className="flex flex-wrap gap-1.5">
                {corrections.map((c, i) => (
                  <span key={i} className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded" data-testid={`correction-${i}`}>
                    {c.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}
          {analysis.action_recommandee && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Action recommandée</p>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-0.5" data-testid="ai-action-recommandee">{analysis.action_recommandee.replace(/_/g, " ")}</p>
            </div>
          )}
          {photo.ai_status === "done" && photo.url.startsWith("/api/uploads/") && (
            <Button variant="outline" size="sm" onClick={() => onReAnalyze(photo.photo_id)} data-testid="btn-re-analyze">
              <Spinner size={14} className="mr-1.5" /> Ré-analyser
            </Button>
          )}
          {photo.ai_status === "done" && (
            <Button size="sm" onClick={() => { window.open(`${API}/vehicles/${photo.vehicle_id}/photos/${photo.photo_id}/download`, "_blank"); }} data-testid="btn-download-photo">
              <DownloadSimple size={14} className="mr-1.5" /> Télécharger
            </Button>
          )}
        </div>
      </div>
    </DialogContent>
  );
};

const PhotosTab = ({ photos, canEditPrep, onAddPhoto, onDeletePhoto, vehicleId, driveConnected, onDriveUpload, drivePhotos, onDeleteDriveFile, loadingDrive, refreshPhotos }) => {
  const [newPhotoUrl, setNewPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const localFileRef = useRef(null);
  const driveFileRef = useRef(null);
  const pollRef = useRef(null);
  const prevStatusRef = useRef({});

  // Poll for AI analysis updates + toast on completion
  useEffect(() => {
    const hasPending = photos.some(p => p.ai_status === "pending" || p.ai_status === "analyzing");
    if (hasPending && refreshPhotos) {
      pollRef.current = setInterval(() => { refreshPhotos(); }, 5000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    // P2: Toast when a photo completes analysis
    photos.forEach(p => {
      const prev = prevStatusRef.current[p.photo_id];
      if (prev && (prev === "pending" || prev === "analyzing") && p.ai_status === "done") {
        const cat = CATEGORY_LABELS[p.ai_analysis?.categorie] || p.ai_analysis?.categorie || "";
        const qual = p.ai_analysis?.qualite?.exploitable ? "exploitable" : "non exploitable";
        toast.success(`Photo analysée : ${cat} — ${qual}`);
      }
      if (prev && (prev === "pending" || prev === "analyzing") && p.ai_status === "error") {
        toast.error("Erreur d'analyse IA sur une photo");
      }
    });
    const newMap = {};
    photos.forEach(p => { newMap[p.photo_id] = p.ai_status; });
    prevStatusRef.current = newMap;
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [photos, refreshPhotos]);

  const handleLocalUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/vehicles/${vehicleId}/photos/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(`${file.name} uploadée — analyse IA lancée`);
      } catch { toast.error(`Erreur upload ${file.name}`); }
    }
    setUploading(false);
    e.target.value = "";
    if (refreshPhotos) refreshPhotos();
  };

  const handleDriveUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      await onDriveUpload(file, "photos");
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleReAnalyze = async (photoId) => {
    try {
      await axios.post(`${API}/vehicles/${vehicleId}/photos/${photoId}/re-analyze`);
      toast.success("Ré-analyse lancée");
      setSelectedPhoto(null);
      if (refreshPhotos) refreshPhotos();
    } catch { toast.error("Erreur ré-analyse"); }
  };

  const allPhotos = [
    ...photos.map(p => ({ ...p, source: "local" })),
    ...(drivePhotos || []).filter(dp => !photos.some(p => p.drive_file_id === dp.drive_file_id)).map(dp => ({ ...dp, source: "drive", photo_id: dp.drive_file_id }))
  ];

  // P1: Filter by category
  const filteredPhotos = categoryFilter === "all"
    ? allPhotos
    : allPhotos.filter(p => (p.categorie || p.ai_analysis?.categorie) === categoryFilter);

  // P2: Stats
  const pendingCount = photos.filter(p => p.ai_status === "pending" || p.ai_status === "analyzing").length;
  const doneCount = photos.filter(p => p.ai_status === "done").length;

  // Categories present for quick filter
  const presentCategories = [...new Set(allPhotos.map(p => p.categorie || p.ai_analysis?.categorie).filter(Boolean))];

  return (
    <div className="card p-6">
      {/* P2: AI summary bar */}
      {photos.length > 0 && (
        <div className="flex items-center gap-4 mb-4 px-3 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700" data-testid="ai-summary-bar">
          <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
            <Eye size={16} /> <span className="font-medium">{photos.length}</span> photos
          </div>
          {doneCount > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-emerald-600">
              <Check size={14} weight="bold" /> <span>{doneCount} analysée{doneCount > 1 ? "s" : ""}</span>
            </div>
          )}
          {pendingCount > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-blue-600 animate-pulse" data-testid="ai-pending-count">
              <Spinner size={14} className="animate-spin" /> <span>{pendingCount} en cours</span>
            </div>
          )}
        </div>
      )}

      {/* Upload bar */}
      <div className="flex flex-wrap gap-2 mb-4">
        {canEditPrep && (
          <>
            <input type="file" accept="image/*" multiple onChange={handleLocalUpload} className="hidden" ref={localFileRef} data-testid="local-photo-file-input" />
            <Button onClick={() => localFileRef.current?.click()} disabled={uploading} className="btn-primary" data-testid="upload-local-photo-btn">
              {uploading ? <Spinner size={18} className="mr-2 animate-spin" /> : <UploadSimple size={18} className="mr-2" />}
              {uploading ? "Upload..." : "Ajouter photos"}
            </Button>
            <div className="flex gap-2 flex-1 min-w-[200px]">
              <Input placeholder="Ou coller une URL..." value={newPhotoUrl} onChange={(e) => setNewPhotoUrl(e.target.value)} data-testid="input-photo-url" />
              <Button variant="outline" onClick={() => { if(newPhotoUrl) { onAddPhoto(newPhotoUrl, photos.length === 0); setNewPhotoUrl(""); }}} data-testid="add-photo-btn"><Plus size={16} className="mr-1" />URL</Button>
            </div>
            {driveConnected && (
              <>
                <input type="file" accept="image/*" multiple onChange={handleDriveUpload} className="hidden" ref={driveFileRef} data-testid="drive-photo-file-input" />
                <Button onClick={() => driveFileRef.current?.click()} disabled={uploading} variant="outline" data-testid="upload-drive-photo-btn">
                  <GoogleDriveLogo size={16} className="mr-1.5" /> Drive
                </Button>
              </>
            )}
          </>
        )}
      </div>

      {/* P1: Category filter bar */}
      {presentCategories.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="category-filter-bar">
          {CATEGORY_FILTERS.filter(f => f.value === "all" || presentCategories.includes(f.value)).map(f => {
            const count = f.value === "all" ? allPhotos.length : allPhotos.filter(p => (p.categorie || p.ai_analysis?.categorie) === f.value).length;
            return (
              <button
                key={f.value}
                onClick={() => setCategoryFilter(f.value)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${categoryFilter === f.value ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"}`}
                data-testid={`filter-${f.value}`}
              >
                {f.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {loadingDrive && <div className="flex items-center gap-2 mb-4 text-neutral-500"><Spinner size={16} className="animate-spin" /> Chargement depuis Drive...</div>}
      {filteredPhotos.length === 0 && allPhotos.length === 0 && !loadingDrive ? (
        <div className="empty-state">
          <Image size={48} className="empty-state-icon" />
          <p className="empty-state-title">Aucune photo</p>
          <p className="empty-state-description">{driveConnected ? "Uploadez des photos depuis votre ordinateur vers Google Drive" : "Ajoutez des photos par URL ou connectez Google Drive dans les Paramètres"}</p>
        </div>
      ) : filteredPhotos.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <p className="text-sm">Aucune photo dans cette catégorie</p>
          <button onClick={() => setCategoryFilter("all")} className="text-xs text-blue-500 mt-1 underline">Voir toutes</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredPhotos.map((photo) => {
            const analysis = photo.ai_analysis || {};
            const correctedUrl = analysis.photo_corrigee_url || photo.photo_corrigee_url;
            const displayUrl = correctedUrl || photo.url;
            const statut = analysis.statut;
            const isProcessing = photo.ai_status === "pending" || photo.ai_status === "analyzing";
            return (
              <div
                key={photo.photo_id || photo.drive_file_id}
                className={`relative group rounded-lg overflow-hidden border bg-slate-50 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-lg ${isProcessing ? "border-blue-300 dark:border-blue-600 ring-1 ring-blue-200 dark:ring-blue-800" : "border-slate-200 dark:border-slate-700"}`}
                onClick={() => setSelectedPhoto(photo)}
                data-testid={`photo-card-${photo.photo_id || photo.drive_file_id}`}
              >
                <div className="relative">
                  <img src={displayUrl} alt="Véhicule" className={`w-full h-40 object-cover transition-opacity ${isProcessing ? "opacity-70" : ""}`} loading="lazy" onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; const ph = e.target.parentNode.querySelector(".photo-placeholder"); if(ph) ph.style.display = "flex"; }} />
                  <div className="photo-placeholder w-full h-40 bg-slate-200 dark:bg-slate-700 items-center justify-center text-slate-400" style={{display:"none"}}><Image size={32} /></div>
                  {/* P2: Scanning overlay for processing photos */}
                  {isProcessing && (
                    <div className="absolute inset-0 bg-blue-500/10 flex items-center justify-center" data-testid="ai-processing-overlay">
                      <div className="bg-white/90 dark:bg-slate-900/90 rounded-lg px-3 py-2 flex items-center gap-2 shadow-sm">
                        <Spinner size={14} className="animate-spin text-blue-600" />
                        <span className="text-xs font-medium text-blue-700 dark:text-blue-300">{photo.ai_status === "pending" ? "En file..." : "Analyse..."}</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* P2: Progress bar for processing */}
                {isProcessing && <AIProgressSteps status={photo.ai_status} />}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {photo.ai_status && !isProcessing && <PhotoAIBadge status={photo.ai_status} />}
                  {statut && !isProcessing && <span className={`text-xs px-2 py-0.5 rounded-full text-white font-medium ${STATUT_COLORS_AI[statut] || "bg-gray-500"}`}>{STATUT_LABELS_AI[statut] || statut}</span>}
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {photo.is_principale && <span className="badge badge-en_stock text-xs">Principale</span>}
                    {photo.source === "drive" && <span className="inline-flex items-center gap-1 text-xs text-white bg-blue-600/80 px-1.5 py-0.5 rounded"><GoogleDriveLogo size={10} /> Drive</span>}
                    {correctedUrl && !isProcessing && <span className="text-xs text-white bg-blue-600/80 px-1.5 py-0.5 rounded">IA</span>}
                    {(photo.categorie || analysis.categorie) && <span className="text-xs text-white/80">{CATEGORY_LABELS[photo.categorie || analysis.categorie] || photo.categorie || analysis.categorie}</span>}
                  </div>
                </div>
                {canEditPrep && (
                  <button
                    onClick={(e) => { e.stopPropagation(); photo.source === "drive" ? onDeleteDriveFile(photo.drive_file_id) : onDeletePhoto(photo.photo_id); }}
                    className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${photo.photo_id || photo.drive_file_id}`}
                  >
                    <Trash size={14} />
                  </button>
                )}
                {photo.ai_status === "done" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); window.open(`${API}/vehicles/${vehicleId}/photos/${photo.photo_id}/download`, "_blank"); }}
                    className="absolute top-2 right-10 bg-blue-600 text-white p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`download-photo-${photo.photo_id}`}
                  >
                    <DownloadSimple size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={!!selectedPhoto} onOpenChange={(open) => { if (!open) setSelectedPhoto(null); }}>
        <PhotoDetailModal photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} onReAnalyze={handleReAnalyze} />
      </Dialog>
    </div>
  );
};

const PLATFORM_TEMPLATES = {
  "2ememain": {
    name: "2ememain.be",
    color: "bg-orange-500",
    generate: (v) => {
      const equips = (v.equipements || []).join(", ");
      return {
        title: `${v.marque} ${v.modele} ${v.version || ""} - ${v.annee} - ${(v.kilometrage || 0).toLocaleString("fr-BE")} km`.trim(),
        description: `${v.marque} ${v.modele} ${v.version || ""} - ${v.annee}

Kilometrage: ${(v.kilometrage || 0).toLocaleString("fr-BE")} km
Carburant: ${v.carburant || "N/A"}
Boite: ${v.boite || "N/A"}
Couleur: ${v.couleur || "N/A"}
${v.nombre_portes ? `Portes: ${v.nombre_portes}` : ""}
${equips ? `\nEquipements: ${equips}` : ""}

${v.description_longue || v.description_courte || "Vehicule en excellent etat, entretien suivi. N'hesitez pas a nous contacter pour plus d'informations ou pour organiser un essai."}

Prix: ${(v.prix_vente || v.prix_affiche || 0).toLocaleString("fr-BE")} EUR

Contact: MikAuto Belgium
`.trim()
      };
    }
  },
  autoscout24: {
    name: "AutoScout24",
    color: "bg-yellow-500",
    generate: (v) => {
      const equips = (v.equipements || []).join(" | ");
      return {
        title: `${v.marque} ${v.modele} ${v.version || ""} ${v.annee}`.trim(),
        description: `A vendre: ${v.marque} ${v.modele} ${v.version || ""}

Annee: ${v.annee}
Km: ${(v.kilometrage || 0).toLocaleString("fr-BE")}
Carburant: ${v.carburant || "N/A"}
Transmission: ${v.boite || "N/A"}
Couleur: ${v.couleur || "N/A"}
VIN: ${v.vin || "N/A"}
${equips ? `\nEquipements:\n${equips}` : ""}

${v.description_longue || v.description_courte || ""}

Prix: ${(v.prix_vente || v.prix_affiche || 0).toLocaleString("fr-BE")} EUR
`.trim()
      };
    }
  },
  facebook: {
    name: "Facebook",
    color: "bg-blue-600",
    generate: (v) => {
      const equips = (v.equipements || []).slice(0, 10).join(" | ");
      return {
        title: `${v.marque} ${v.modele} ${v.annee} - ${(v.prix_vente || v.prix_affiche || 0).toLocaleString("fr-BE")} EUR`,
        description: `${v.marque} ${v.modele} ${v.version || ""} - ${v.annee}
${(v.kilometrage || 0).toLocaleString("fr-BE")} km | ${v.carburant || ""} | ${v.boite || ""}
${equips ? `\n${equips}` : ""}

${v.description_longue || v.description_courte || "Beau vehicule, bien entretenu."}

Prix: ${(v.prix_vente || v.prix_affiche || 0).toLocaleString("fr-BE")} EUR
Contactez-nous pour essai !`.trim()
      };
    }
  }
};

const AnnonceTab = ({ vehicleForm, setVehicleForm, canEdit, saving, onSave }) => {
  const [selectedPlatform, setSelectedPlatform] = useState("2ememain");
  const [generatedTitle, setGeneratedTitle] = useState("");
  const [generatedDesc, setGeneratedDesc] = useState("");
  const [copied, setCopied] = useState(null);

  const handleGenerate = (platform) => {
    setSelectedPlatform(platform);
    const tpl = PLATFORM_TEMPLATES[platform];
    if (tpl) {
      const result = tpl.generate(vehicleForm);
      setGeneratedTitle(result.title);
      setGeneratedDesc(result.description);
    }
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch { /* fallback */ }
  };

  const handleCopyAll = async () => {
    const full = `${generatedTitle}\n\n${generatedDesc}`;
    await handleCopy(full, "all");
  };

  return (
    <div className="space-y-5">
      {/* Custom annonce fields */}
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Megaphone size={16} className="text-blue-600" /> Informations annonce
        </h3>
        <div><Label className="form-label">Titre</Label><Input value={vehicleForm.titre_annonce || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, titre_annonce: e.target.value })} disabled={!canEdit} placeholder={`${vehicleForm?.marque || ''} ${vehicleForm?.modele || ''} ${vehicleForm?.annee || ''}`} data-testid="annonce-titre" /></div>
        <div><Label className="form-label">Description courte</Label><Textarea value={vehicleForm.description_courte || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, description_courte: e.target.value })} disabled={!canEdit} rows={2} data-testid="annonce-desc-courte" /></div>
        <div><Label className="form-label">Description longue</Label><Textarea value={vehicleForm.description_longue || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, description_longue: e.target.value })} disabled={!canEdit} rows={5} data-testid="annonce-desc-longue" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="form-label">Prix affiche (EUR)</Label><Input type="number" step="0.01" value={vehicleForm.prix_affiche || vehicleForm.prix_vente || 0} onChange={(e) => setVehicleForm({ ...vehicleForm, prix_affiche: parseFloat(e.target.value) || 0 })} disabled={!canEdit} /></div>
          <div><Label className="form-label">Lien annonce</Label><Input value={vehicleForm.lien_annonce || ""} onChange={(e) => setVehicleForm({ ...vehicleForm, lien_annonce: e.target.value })} disabled={!canEdit} placeholder="https://..." data-testid="annonce-lien" /></div>
        </div>
        {canEdit && (
          <div className="flex justify-end"><Button onClick={onSave} disabled={saving} className="btn-primary" data-testid="annonce-save-btn"><FloppyDisk size={16} className="mr-1.5" />{saving ? "..." : "Enregistrer"}</Button></div>
        )}
      </div>

      {/* Platform generator */}
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <ShareNetwork size={16} className="text-violet-600" /> Generateur d'annonce
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">Generez automatiquement une annonce formatee pour chaque plateforme, puis copiez-la.</p>

        <div className="flex gap-2 flex-wrap">
          {Object.entries(PLATFORM_TEMPLATES).map(([key, tpl]) => (
            <Button key={key} size="sm" variant={selectedPlatform === key ? "default" : "outline"} onClick={() => handleGenerate(key)}
              className={selectedPlatform === key ? `${tpl.color} text-white` : ""}
              data-testid={`annonce-gen-${key}`}>
              {tpl.name}
            </Button>
          ))}
        </div>

        {generatedTitle && (
          <div className="space-y-3 mt-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="form-label">Titre genere</Label>
                <button onClick={() => handleCopy(generatedTitle, "title")} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="copy-title-btn">
                  {copied === "title" ? <><Check size={12} /> Copie !</> : <><Copy size={12} /> Copier</>}
                </button>
              </div>
              <Input value={generatedTitle} onChange={e => setGeneratedTitle(e.target.value)} data-testid="annonce-generated-title" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="form-label">Description generee</Label>
                <button onClick={() => handleCopy(generatedDesc, "desc")} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="copy-desc-btn">
                  {copied === "desc" ? <><Check size={12} /> Copie !</> : <><Copy size={12} /> Copier</>}
                </button>
              </div>
              <Textarea value={generatedDesc} onChange={e => setGeneratedDesc(e.target.value)} rows={10} data-testid="annonce-generated-desc" />
            </div>
            <Button onClick={handleCopyAll} variant="outline" className="w-full" data-testid="copy-all-btn">
              {copied === "all" ? <><Check size={14} className="mr-1.5" /> Tout copie !</> : <><Copy size={14} className="mr-1.5" /> Copier titre + description</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const ScoreGauge = ({ score }) => {
  const color = score >= 75 ? "text-emerald-600" : score >= 50 ? "text-amber-500" : "text-red-600";
  const bgColor = score >= 75 ? "bg-emerald-100 dark:bg-emerald-900/30" : score >= 50 ? "bg-amber-100 dark:bg-amber-900/30" : "bg-red-100 dark:bg-red-900/30";
  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${bgColor}`} data-testid="audit-score">
      <span className={`text-3xl font-black ${color}`}>{score}</span>
      <span className="text-sm text-slate-500 dark:text-slate-400">/100</span>
    </div>
  );
};

const AuditTab = ({ vehicleId, audits, canAudit, onRefreshData }) => {
  const [creating, setCreating] = useState(false);
  const [editingAudit, setEditingAudit] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [frais, setFrais] = useState([]);
  const [decision, setDecision] = useState("en_attente");
  const [decisionComment, setDecisionComment] = useState("");
  const [notesGenerales, setNotesGenerales] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [photoCategory, setPhotoCategory] = useState("");

  const startNewAudit = async () => {
    try {
      const res = await axios.get(`${API}/audits/checklist-template`);
      setChecklist(res.data.checklist);
      setFrais([]);
      setDecision("en_attente");
      setDecisionComment("");
      setNotesGenerales("");
      setCreating(true);
      setEditingAudit(null);
      setExpandedCats(Object.keys(res.data.checklist).reduce((a, k) => ({...a, [k]: true}), {}));
    } catch { toast.error("Erreur chargement checklist"); }
  };

  const openEditAudit = (audit) => {
    setChecklist(audit.checklist || {});
    setFrais(audit.frais || []);
    setDecision(audit.decision || "en_attente");
    setDecisionComment(audit.decision_commentaire || "");
    setNotesGenerales(audit.notes_generales || "");
    setEditingAudit(audit);
    setCreating(true);
    setExpandedCats(Object.keys(audit.checklist || {}).reduce((a, k) => ({...a, [k]: true}), {}));
  };

  const updateItemStatus = (catKey, itemIdx, newStatus) => {
    setChecklist(prev => {
      const newCl = JSON.parse(JSON.stringify(prev));
      newCl[catKey].items[itemIdx].status = newStatus;
      return newCl;
    });
  };

  const updateItemNote = (catKey, itemIdx, note) => {
    setChecklist(prev => {
      const newCl = JSON.parse(JSON.stringify(prev));
      newCl[catKey].items[itemIdx].note = note;
      return newCl;
    });
  };

  const addFrais = () => setFrais([...frais, { label: "", montant: 0 }]);
  const removeFrais = (idx) => setFrais(frais.filter((_, i) => i !== idx));
  const updateFrais = (idx, field, value) => {
    const nf = [...frais];
    nf[idx] = { ...nf[idx], [field]: field === "montant" ? parseFloat(value) || 0 : value };
    setFrais(nf);
  };

  const totalFrais = frais.reduce((s, f) => s + (f.montant || 0), 0);

  const calcScore = () => {
    let total = 0, pts = 0;
    Object.values(checklist).forEach(cat => {
      (cat.items || []).forEach(item => {
        total++;
        if (item.status === "ok") pts += 3;
        else if (item.status === "a_prevoir") pts += 1;
      });
    });
    return total > 0 ? Math.round((pts / (total * 3)) * 100) : 0;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { vehicle_id: vehicleId, checklist, frais, decision, decision_commentaire: decisionComment, notes_generales: notesGenerales };
      if (editingAudit) {
        await axios.put(`${API}/audits/${editingAudit.audit_id}`, payload);
        toast.success("Audit mis a jour");
      } else {
        await axios.post(`${API}/audits`, payload);
        toast.success("Audit cree");
      }
      setCreating(false);
      setEditingAudit(null);
      onRefreshData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDeleteAudit = async (auditId) => {
    try {
      await axios.delete(`${API}/audits/${auditId}`);
      toast.success("Audit supprime");
      onRefreshData();
    } catch { toast.error("Erreur suppression"); }
  };

  const handlePhotoUpload = async (e, auditId, category) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const catParam = category ? `?category=${encodeURIComponent(category)}` : "";
      await axios.post(`${API}/audits/${auditId}/photos${catParam}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo ajoutee");
      onRefreshData();
    } catch { toast.error("Erreur upload photo"); }
    finally { setUploadingPhoto(false); e.target.value = ""; }
  };

  const handleDeletePhoto = async (auditId, filename) => {
    try {
      await axios.delete(`${API}/audits/${auditId}/photos/${filename}`);
      toast.success("Photo supprimee");
      onRefreshData();
    } catch { toast.error("Erreur suppression photo"); }
  };

  const startRecording = async (auditId, category) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("file", blob, `note_${Date.now()}.webm`);
        const catParam = category ? `?category=${encodeURIComponent(category)}` : "";
        try {
          await axios.post(`${API}/audits/${auditId}/audio${catParam}`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          toast.success("Note audio enregistree");
          onRefreshData();
        } catch { toast.error("Erreur upload audio"); }
      };
      mr.start();
      setMediaRecorder(mr);
      setRecording(true);
    } catch { toast.error("Acces au microphone refuse"); }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
    }
    setRecording(false);
    setMediaRecorder(null);
  };

  const handleDeleteAudio = async (auditId, filename) => {
    try {
      await axios.delete(`${API}/audits/${auditId}/audio/${filename}`);
      toast.success("Note audio supprimee");
      onRefreshData();
    } catch { toast.error("Erreur suppression audio"); }
  };

  const toggleCat = (key) => setExpandedCats(prev => ({...prev, [key]: !prev[key]}));

  // EDIT / CREATE MODE
  if (creating) {
    const score = calcScore();
    return (
      <div className="space-y-6" data-testid="audit-editor">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{editingAudit ? "Modifier l'audit" : "Nouvel audit avant achat"}</h3>
          <div className="flex items-center gap-3">
            <ScoreGauge score={score} />
            <Button variant="outline" onClick={() => { setCreating(false); setEditingAudit(null); }}>Annuler</Button>
            <Button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-audit-btn">
              <FloppyDisk size={16} className="mr-1.5" /> {saving ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
          </div>
        </div>

        {Object.entries(checklist).map(([catKey, catData]) => {
          const CatIcon = CAT_ICONS[catKey] || ClipboardText;
          const catItems = catData.items || [];
          const catOk = catItems.filter(i => i.status === "ok").length;
          const catProb = catItems.filter(i => i.status === "probleme").length;
          const isExpanded = expandedCats[catKey];
          return (
            <div key={catKey} className="card overflow-hidden" data-testid={`audit-cat-${catKey}`}>
              <button onClick={() => toggleCat(catKey)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left">
                <div className="flex items-center gap-3">
                  <CatIcon size={20} className="text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">{catData.label}</span>
                  <span className="text-xs text-slate-400">{catItems.length} points</span>
                </div>
                <div className="flex items-center gap-2">
                  {catOk > 0 && <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{catOk} OK</span>}
                  {catProb > 0 && <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">{catProb} Pb</span>}
                  <CaretLeft size={16} className={`text-slate-400 transition-transform ${isExpanded ? "-rotate-90" : ""}`} />
                </div>
              </button>
              {isExpanded && (
                <div className="border-t border-slate-100 dark:border-slate-800">
                  {catItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 dark:border-slate-800/50 last:border-0">
                      <div className="flex gap-1">
                        {["ok", "a_prevoir", "probleme"].map(s => (
                          <button key={s} onClick={() => updateItemStatus(catKey, idx, item.status === s ? "non_verifie" : s)}
                            data-testid={`audit-item-${catKey}-${idx}-${s}`}
                            className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold transition-all ${
                              item.status === s ? AUDIT_STATUS_COLORS[s] : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                            }`}>
                            {s === "ok" ? <Check size={14} weight="bold" /> : s === "a_prevoir" ? "!" : <X size={14} weight="bold" />}
                          </button>
                        ))}
                      </div>
                      <span className="flex-1 text-sm text-slate-700 dark:text-slate-300">{item.label}</span>
                      <Input className="w-40 h-7 text-xs" placeholder="Note..." value={item.note || ""} onChange={e => updateItemNote(catKey, idx, e.target.value)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100">Estimation des frais</h4>
            <Button size="sm" variant="outline" onClick={addFrais} data-testid="add-frais-btn"><Plus size={14} className="mr-1" /> Ajouter</Button>
          </div>
          {frais.length === 0 ? (
            <p className="text-sm text-slate-400">Aucun frais estime</p>
          ) : (
            <div className="space-y-2">
              {frais.map((f, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <Input className="flex-1 h-8 text-sm" placeholder="Ex: Carrosserie" value={f.label} onChange={e => updateFrais(idx, "label", e.target.value)} data-testid={`frais-label-${idx}`} />
                  <Input className="w-28 h-8 text-sm text-right" type="number" step="0.01" placeholder="0 EUR" value={f.montant || ""} onChange={e => updateFrais(idx, "montant", e.target.value)} data-testid={`frais-montant-${idx}`} />
                  <button onClick={() => removeFrais(idx)} className="p-1 text-red-400 hover:text-red-600"><Trash size={16} /></button>
                </div>
              ))}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <span className="text-sm text-slate-500">Total :</span>
                <span className="text-lg font-bold text-slate-900 dark:text-slate-100" data-testid="frais-total">{totalFrais.toLocaleString('fr-BE')} EUR</span>
              </div>
            </div>
          )}
        </div>

        <div className="card p-5">
          <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 mb-3">Notes generales</h4>
          <Textarea value={notesGenerales} onChange={e => setNotesGenerales(e.target.value)} placeholder="Observations, remarques..." rows={3} data-testid="audit-notes" />
        </div>

        <div className="card p-5">
          <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 mb-4">Decision finale</h4>
          <div className="flex gap-3 mb-4">
            {["acheter", "negocier", "refuser"].map(d => (
              <Button key={d} onClick={() => setDecision(d)} data-testid={`decision-${d}`}
                className={`flex-1 ${decision === d ? DECISION_COLORS[d] : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"}`}>
                {DECISION_LABELS[d]}
              </Button>
            ))}
          </div>
          <Input placeholder="Commentaire decision..." value={decisionComment} onChange={e => setDecisionComment(e.target.value)} data-testid="decision-comment" />
        </div>

        {/* Media section - only for existing audits */}
        {editingAudit && (
          <div className="card p-5" data-testid="audit-media-section">
            <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <Image size={16} className="text-blue-600" /> Photos & Notes audio
            </h4>

            {/* Photos */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Photos ({(editingAudit.photos_audit || []).length})</span>
                <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors" data-testid="audit-upload-photo-btn">
                  <UploadSimple size={14} /> Ajouter photo
                  <input type="file" accept="image/*" className="hidden" onChange={e => handlePhotoUpload(e, editingAudit.audit_id, photoCategory)} disabled={uploadingPhoto} />
                </label>
              </div>
              {(editingAudit.photos_audit || []).length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {(editingAudit.photos_audit || []).map((photo, idx) => (
                    <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800">
                      <img src={`${API.replace('/api', '')}${photo.url || photo}`} alt={`Audit photo ${idx + 1}`} className="w-full h-full object-cover" />
                      <button onClick={() => handleDeletePhoto(editingAudit.audit_id, photo.filename || photo.url?.split('/').pop())} className="absolute top-1 right-1 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-audit-photo-${idx}`}>
                        <X size={10} weight="bold" />
                      </button>
                      {photo.category && <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 text-center truncate">{photo.category}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Aucune photo. Ajoutez des photos de l'etat du vehicule.</p>
              )}
            </div>

            {/* Audio Notes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Notes audio ({(editingAudit.audio_notes || []).length})</span>
                {recording ? (
                  <button onClick={stopRecording} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 rounded-lg animate-pulse" data-testid="audit-stop-recording-btn">
                    <X size={14} /> Arreter
                  </button>
                ) : (
                  <button onClick={() => startRecording(editingAudit.audit_id, "")} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors" data-testid="audit-record-audio-btn">
                    <Plus size={14} /> Enregistrer note
                  </button>
                )}
              </div>
              {(editingAudit.audio_notes || []).length > 0 ? (
                <div className="space-y-2">
                  {(editingAudit.audio_notes || []).map((audio, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                      <audio controls src={`${API.replace('/api', '')}${audio.url}`} className="h-8 flex-1" />
                      {audio.category && <span className="text-[10px] text-slate-400 px-1">{audio.category}</span>}
                      <button onClick={() => handleDeleteAudio(editingAudit.audit_id, audio.filename)} className="p-1 text-red-400 hover:text-red-600" data-testid={`delete-audit-audio-${idx}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Aucune note audio. Enregistrez des observations vocales.</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // LIST MODE
  return (
    <div className="space-y-4" data-testid="audit-list">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Audits ({audits.length})</h3>
        {canAudit && (
          <Button onClick={startNewAudit} className="btn-primary" data-testid="new-audit-btn">
            <Plus size={16} className="mr-1.5" /> Nouvel audit
          </Button>
        )}
      </div>

      {audits.length === 0 ? (
        <div className="card p-12 text-center">
          <ClipboardText size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Aucun audit pour ce vehicule</p>
          {canAudit && <p className="text-xs text-slate-400 mt-1">Creez un audit avant achat pour evaluer le vehicule</p>}
        </div>
      ) : (
        audits.map(audit => {
          const score = audit.score || 0;
          const scoreColor = score >= 75 ? "text-emerald-600" : score >= 50 ? "text-amber-500" : "text-red-600";
          let okCount = 0, apCount = 0, pbCount = 0;
          Object.values(audit.checklist || {}).forEach(cat => {
            (cat.items || []).forEach(i => {
              if (i.status === "ok") okCount++;
              else if (i.status === "a_prevoir") apCount++;
              else if (i.status === "probleme") pbCount++;
            });
          });
          return (
            <div key={audit.audit_id} className="card p-5 hover:shadow-md transition-all" data-testid={`audit-card-${audit.audit_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{new Date(audit.date_audit).toLocaleDateString("fr-BE", { day: "2-digit", month: "long", year: "numeric" })}</span>
                    {audit.user_name && <span className="text-xs text-slate-400">par {audit.user_name}</span>}
                  </div>
                  <div className="flex gap-2">
                    {okCount > 0 && <span className={`px-2 py-0.5 text-xs rounded-full ${AUDIT_STATUS_COLORS.ok}`}>{okCount} OK</span>}
                    {apCount > 0 && <span className={`px-2 py-0.5 text-xs rounded-full ${AUDIT_STATUS_COLORS.a_prevoir}`}>{apCount} A prevoir</span>}
                    {pbCount > 0 && <span className={`px-2 py-0.5 text-xs rounded-full ${AUDIT_STATUS_COLORS.probleme}`}>{pbCount} Probleme</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className={`text-2xl font-black ${scoreColor}`}>{score}</span>
                    <span className="text-xs text-slate-400">/100</span>
                  </div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${DECISION_COLORS[audit.decision]}`}>{DECISION_LABELS[audit.decision] || "En attente"}</span>
                </div>
              </div>
              {audit.total_frais > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-2">
                  <span>Frais estimes :</span><span className="font-semibold text-slate-900 dark:text-slate-100">{audit.total_frais.toLocaleString('fr-BE')} EUR</span>
                </div>
              )}
              {audit.notes_generales && <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded p-2 mb-2">{audit.notes_generales}</p>}
              {/* Media badges */}
              {((audit.photos_audit || []).length > 0 || (audit.audio_notes || []).length > 0) && (
                <div className="flex gap-2 mb-2">
                  {(audit.photos_audit || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
                      <Image size={10} /> {(audit.photos_audit || []).length} photo(s)
                    </span>
                  )}
                  {(audit.audio_notes || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300">
                      {(audit.audio_notes || []).length} audio(s)
                    </span>
                  )}
                </div>
              )}
              {canAudit && (
                <div className="flex gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => openEditAudit(audit)} data-testid={`edit-audit-${audit.audit_id}`}>
                    <PencilSimple size={13} className="mr-1" /> Modifier
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-xs h-7 text-red-500 hover:text-red-600 ml-auto" data-testid={`delete-audit-${audit.audit_id}`}>
                        <Trash size={13} className="mr-1" /> Supprimer
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Supprimer cet audit ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteAudit(audit.audit_id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

/* ─── Main Component ─── */

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [vehicle, setVehicle] = useState(null);
  const [preparation, setPreparation] = useState(null);
  const [prepTasks, setPrepTasks] = useState([]);
  const [prestataires, setPrestataires] = useState([]);
  const [prepTypes, setPrepTypes] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("infos");
  const [vehicleForm, setVehicleForm] = useState({});
  const [docsForm, setDocsForm] = useState({});
  const [driveConnected, setDriveConnected] = useState(false);
  const [drivePhotos, setDrivePhotos] = useState([]);
  const [driveDocuments, setDriveDocuments] = useState([]);
  const [loadingDrive, setLoadingDrive] = useState(false);

  const canEdit = user?.role === "admin" || user?.role === "commercial";
  const canEditPrep = user?.role === "admin" || user?.role === "commercial" || user?.role === "preparateur";
  const canAudit = user?.role === "admin" || user?.role === "auditeur" || user?.role === "preparateur";
  const canDelete = user?.role === "admin";
  const canSeePrices = user?.role === "admin" || user?.role === "commercial" || user?.role === "comptabilite";

  const fetchData = useCallback(async () => {
    try {
      const [vehicleRes, prepRes, docsRes, photosRes, auditsRes, tasksRes, prestRes, typesRes] = await Promise.all([
        axios.get(`${API}/vehicles/${id}`),
        axios.get(`${API}/vehicles/${id}/preparation`).catch(() => ({ data: null })),
        axios.get(`${API}/vehicles/${id}/documents`).catch(() => ({ data: { checklist: null, documents: [] } })),
        axios.get(`${API}/vehicles/${id}/photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/vehicles/${id}/audits`).catch(() => ({ data: [] })),
        axios.get(`${API}/vehicles/${id}/preparation-tasks`).catch(() => ({ data: [] })),
        axios.get(`${API}/prestataires`).catch(() => ({ data: [] })),
        axios.get(`${API}/preparation-types`).catch(() => ({ data: [] })),
      ]);
      setVehicle(vehicleRes.data);
      setVehicleForm(vehicleRes.data);
      setPreparation(prepRes.data);
      setDocsForm(docsRes.data?.checklist || {});
      setPhotos(photosRes.data || []);
      setAudits(auditsRes.data || []);
      setPrepTasks(tasksRes.data || []);
      setPrestataires(prestRes.data || []);
      setPrepTypes(typesRes.data || []);
    } catch (error) {
      console.error("Error fetching vehicle:", error);
      toast.error("Erreur lors du chargement du véhicule");
      navigate("/vehicules");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check Drive status and fetch Drive files
  const fetchDriveData = useCallback(async () => {
    try {
      const statusRes = await axios.get(`${API}/drive/status`).catch(() => ({ data: { connected: false } }));
      setDriveConnected(statusRes.data?.connected || false);
      if (statusRes.data?.connected) {
        setLoadingDrive(true);
        const [photosRes, docsRes] = await Promise.all([
          axios.get(`${API}/vehicles/${id}/drive/photos`).catch(() => ({ data: [] })),
          axios.get(`${API}/vehicles/${id}/drive/documents`).catch(() => ({ data: [] }))
        ]);
        setDrivePhotos(photosRes.data || []);
        setDriveDocuments(docsRes.data || []);
        setLoadingDrive(false);
      }
    } catch {
      setDriveConnected(false);
      setLoadingDrive(false);
    }
  }, [id]);

  useEffect(() => { fetchDriveData(); }, [fetchDriveData]);

  const handleSaveVehicle = async () => {
    setSaving(true);
    try {
      const { vehicle_id, created_at, updated_at, created_by, reference_interne, ...updateData } = vehicleForm;
      await axios.put(`${API}/vehicles/${id}`, updateData);
      toast.success("Véhicule mis à jour");
      setVehicle(vehicleForm);
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de la sauvegarde";
      toast.error(msg);
      if (error.response?.status === 400) {
        // Revert status change in form
        setVehicleForm(prev => ({ ...prev, statut: vehicle?.statut }));
      }
    }
    finally { setSaving(false); }
  };

  const handleUpdatePrepStep = async (step, statut, commentaire) => {
    try {
      await axios.put(`${API}/vehicles/${id}/preparation/step`, { step, statut, commentaire });
      toast.success(`${PREP_STEP_LABELS[step]} mis à jour`);
      fetchData();
    } catch (error) { toast.error("Erreur lors de la mise à jour"); }
  };

  const handleSaveDocuments = async () => {
    setSaving(true);
    try {
      const { checklist_id, vehicle_id, updated_at, updated_by, ...updateData } = docsForm;
      await axios.put(`${API}/vehicles/${id}/documents/checklist`, updateData);
      toast.success("Documents mis à jour");
      fetchData();
    } catch (error) { toast.error("Erreur lors de la sauvegarde"); }
    finally { setSaving(false); }
  };

  const handleAddPhoto = async (url, isPrincipale) => {
    if (!url) return;
    try {
      await axios.post(`${API}/vehicles/${id}/photos`, null, { params: { url, is_principale: isPrincipale } });
      toast.success("Photo ajoutée");
      fetchData();
    } catch (error) { toast.error("Erreur lors de l'ajout"); }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/vehicles/${id}/photos/${photoId}`);
      toast.success("Photo supprimée");
      fetchData();
    } catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  const handleCreateAudit = async (auditData) => {
    try {
      await axios.post(`${API}/audits`, { vehicle_id: id, ...auditData });
      toast.success("Audit créé");
      fetchData();
    } catch (error) { toast.error("Erreur lors de la création de l'audit"); }
  };

  const handleDriveUpload = async (file, type) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const endpoint = type === "photos" ? "drive/photos" : "drive/documents";
      await axios.post(`${API}/vehicles/${id}/${endpoint}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${type === "photos" ? "Photo" : "Document"} uploadé sur Drive`);
      fetchDriveData();
      if (type === "photos") fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de l'upload";
      toast.error(msg);
    }
  };

  const handleDeleteDriveFile = async (driveFileId) => {
    try {
      await axios.delete(`${API}/vehicles/${id}/drive/files/${driveFileId}`);
      toast.success("Fichier supprimé de Drive");
      fetchDriveData();
      fetchData();
    } catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  const handleDeleteVehicle = async () => {
    try {
      await axios.delete(`${API}/vehicles/${id}`);
      toast.success("Véhicule archivé");
      navigate("/vehicules");
    } catch (error) { toast.error("Erreur lors de la suppression"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner h-10 w-10"></div></div>;
  }

  const progression = prepTasks.length > 0
    ? Math.round((prepTasks.filter(t => t.statut === "termine").length / prepTasks.length) * 100)
    : (preparation?.progression || 0);
  const aiPendingCount = photos.filter(p => p.ai_status === "pending" || p.ai_status === "analyzing").length;
  const TABS = [
    { value: "infos", label: "Informations" }, { value: "preparation", label: "Préparation" },
    { value: "documents", label: "Documents" }, { value: "photos", label: aiPendingCount > 0 ? `Photos (${aiPendingCount})` : "Photos" },
    { value: "annonce", label: "Annonce" }, { value: "audit", label: "Audit" }
  ];

  return (
    <div data-testid="vehicle-detail-page" className="animate-fade-in">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/vehicules" data-testid="back-to-vehicles" className="p-2 hover:bg-slate-100 rounded-lg transition-colors"><CaretLeft size={20} /></Link>
          <div>
            <h1 className="page-title">{vehicle?.marque} {vehicle?.modele}</h1>
            <p className="page-subtitle">{vehicle?.reference_interne} &middot; {vehicle?.immatriculation || vehicle?.vin}</p>
          </div>
          <span className={`badge badge-${vehicle?.statut}`}>{STATUS_LABELS[vehicle?.statut]}</span>
          {vehicle?.type_propriete === "depot_vente" && <span className="px-2 py-0.5 text-[10px] rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 font-semibold">DEPOT-VENTE</span>}
        </div>
        <div className="page-actions">
          <Button variant="outline" onClick={async () => {
            try {
              const res = await axios.get(`${API}/vehicles/${id}/qrcode`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a"); a.href = url; a.download = `qr_${vehicle?.reference_interne || id}.png`;
              document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
              toast.success("QR Code telecharge !");
            } catch { toast.error("Erreur generation QR Code"); }
          }} data-testid="qr-code-btn"><QrCode size={18} className="mr-2" /> QR Code</Button>
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-red-600 hover:text-red-700" data-testid="delete-vehicle-btn"><Trash size={18} className="mr-2" /> Supprimer</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Archiver ce véhicule ?</AlertDialogTitle><AlertDialogDescription>Le véhicule sera archivé.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={handleDeleteVehicle} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Archiver</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-caption text-slate-500">Progression préparation</span>
          <span className="text-caption font-semibold text-slate-700">{progression}%</span>
        </div>
        <div className="progress"><div className={`progress-bar ${progression === 100 ? 'progress-bar-success' : progression >= 60 ? '' : 'progress-bar-warning'}`} style={{ width: `${progression}%` }} /></div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="tabs mb-6">
          <TabsList className="tabs-list bg-transparent p-0 h-auto">
            {TABS.map(t => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}
                className="tab data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 rounded-none border-b-2 border-transparent relative">
                {t.label}
                {t.value === "photos" && aiPendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse" data-testid="photos-tab-ai-dot" />
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value="infos"><VehicleInfoTab vehicleForm={vehicleForm} setVehicleForm={setVehicleForm} canEdit={canEdit} canSeePrices={canSeePrices} saving={saving} onSave={handleSaveVehicle} prepProgression={progression} vehicleId={id} /></TabsContent>
        <TabsContent value="preparation">
          <PreparationTab
            vehicleId={id}
            canEditPrep={canEditPrep}
            prepTasks={prepTasks}
            setPrepTasks={setPrepTasks}
            prestataires={prestataires}
            prepTypes={prepTypes}
            localisation={vehicle?.localisation}
            onRefreshData={fetchData}
          />
        </TabsContent>
        <TabsContent value="documents"><DocumentsTab docsForm={docsForm} setDocsForm={setDocsForm} canEdit={canEdit} saving={saving} onSave={handleSaveDocuments} driveConnected={driveConnected} onDriveUpload={handleDriveUpload} driveDocuments={driveDocuments} onDeleteDriveFile={handleDeleteDriveFile} /></TabsContent>
        <TabsContent value="photos"><PhotosTab photos={photos} canEditPrep={canEditPrep} onAddPhoto={handleAddPhoto} onDeletePhoto={handleDeletePhoto} vehicleId={id} driveConnected={driveConnected} onDriveUpload={handleDriveUpload} drivePhotos={drivePhotos} onDeleteDriveFile={handleDeleteDriveFile} loadingDrive={loadingDrive} refreshPhotos={fetchData} /></TabsContent>
        <TabsContent value="annonce"><AnnonceTab vehicleForm={vehicleForm} setVehicleForm={setVehicleForm} canEdit={canEdit} saving={saving} onSave={handleSaveVehicle} /></TabsContent>
        <TabsContent value="audit"><AuditTab vehicleId={id} audits={audits} canAudit={canAudit} onRefreshData={fetchData} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default VehicleDetail;
